import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { parsePhoneNumber } from "awesome-phonenumber";
import { TextField } from "@mui/material";
import { useWidgetConfig, useThemeFonts, WidgetEditUnsuppressProvider, LoginDialogWidget } from "@brandclub/bc-widgets";
import { useConciergeContext } from "../../ConciergeContextProvider.js";
import { useAppConfig } from "../../../api/cache/appConfigQueryOptions.js";
import { getBrandLogo } from "../../../utils/assetUtils.js";
import { ResponsiveDialog } from "../../../Components/Dialog/ResponsiveDialog.js";
function resolveFontFamilyCss(fontKey, themeFonts) {
  if (!fontKey || fontKey === "inherit") return void 0;
  const fontData = themeFonts[fontKey];
  if (!fontData) return void 0;
  return fontData.format === "system" ? fontData.url : `"${fontData.name}", sans-serif`;
}
function loadGoogleFont(url) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}
const isPhoneValid = (phone) => parsePhoneNumber(phone, { regionCode: "US" }).valid;
const applyPhoneMask = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const toDigits = (value) => value.replace(/\D/g, "");
function AnimatedHeight({ children }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const initialized = useRef(false);
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const setHeight = (animated) => {
      const h = inner.offsetHeight;
      if (!animated) {
        outer.style.transition = "none";
        outer.style.height = `${h}px`;
        void outer.offsetHeight;
        outer.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
        initialized.current = true;
      } else {
        outer.style.height = `${h}px`;
      }
    };
    const observer = new ResizeObserver(() => setHeight(initialized.current));
    observer.observe(inner);
    setHeight(false);
    return () => observer.disconnect();
  }, []);
  return /* @__PURE__ */ jsx("div", { ref: outerRef, style: { overflow: "hidden" }, children: /* @__PURE__ */ jsx("div", { ref: innerRef, children }) });
}
const Spinner = ({
  size = 48,
  color = "#0D0D0D",
  trackColor = "#E0E0E0"
}) => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    style: { animation: "login-dialog-spin 0.9s linear infinite", flexShrink: 0 },
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "24", cy: "24", r: "18", stroke: trackColor, strokeWidth: "5" }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M42 24A18 18 0 0 0 24 6",
          stroke: color,
          strokeWidth: "5",
          strokeLinecap: "round"
        }
      )
    ]
  }
);
function LoginModalContent({
  onClose,
  brandLogoUrl,
  styles,
  resolveColor
}) {
  const { inlineSignIn, inlineVerifyOtp } = useConciergeContext();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((v) => v - 1), 1e3);
    return () => clearTimeout(id);
  }, [resendCooldown]);
  useEffect(() => {
    var _a, _b;
    if (step === "phone") (_a = phoneInputRef.current) == null ? void 0 : _a.focus();
    if (step === "otp") (_b = otpInputRef.current) == null ? void 0 : _b.focus();
  }, [step]);
  const submitPhone = async (e) => {
    var _a;
    e == null ? void 0 : e.preventDefault();
    if (!inlineSignIn || step !== "phone" || submitting) return;
    setError(null);
    setSubmitting(true);
    const e164 = ((_a = parsePhoneNumber(phone, { regionCode: "US" }).number) == null ? void 0 : _a.e164) ?? phone;
    const res = await inlineSignIn(e164);
    setSubmitting(false);
    if ("error" in res) {
      setError(res.error);
    } else {
      setSession(res.session);
      setStep("otp");
      setResendCooldown(30);
    }
  };
  const verifyOtp = async (e) => {
    e == null ? void 0 : e.preventDefault();
    if (!inlineVerifyOtp || step !== "otp") return;
    setError(null);
    setStep("loading");
    const res = await inlineVerifyOtp(session, otp);
    if (res.success) {
      onClose();
    } else {
      setStep("otp");
      setError(res.error);
    }
  };
  const resend = async () => {
    var _a;
    if (!inlineSignIn || resendCooldown > 0) return;
    setError(null);
    const e164 = ((_a = parsePhoneNumber(phone, { regionCode: "US" }).number) == null ? void 0 : _a.e164) ?? phone;
    const res = await inlineSignIn(e164);
    if ("error" in res) {
      setError(res.error);
    } else {
      setSession(res.session);
      setResendCooldown(30);
      setOtp("");
    }
  };
  const titleColor = resolveColor(styles.titleColor);
  const subtitleColor = resolveColor(styles.subtitleColor);
  if (step === "loading") {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          padding: styles.dialogPadding,
          textAlign: "center"
        },
        children: [
          /* @__PURE__ */ jsx("p", { style: { fontSize: styles.titleFontSize, fontWeight: 600, color: titleColor, margin: 0, lineHeight: "25px" }, children: "Almost there!" }),
          /* @__PURE__ */ jsx("p", { style: { fontSize: styles.subtitleFontSize, fontWeight: 500, color: subtitleColor, margin: 0, lineHeight: "1.5", maxWidth: 280 }, children: "We're verifying your information, it will only take a second." }),
          /* @__PURE__ */ jsx(Spinner, { size: 48, color: titleColor, trackColor: "rgba(0,0,0,0.1)" })
        ]
      }
    );
  }
  const isPhone = step === "phone";
  const canContinue = (isPhone ? isPhoneValid(phone) : otp.length > 0) && !submitting;
  const title = isPhone ? "Welcome" : "Just confirming it's you";
  const subtitle = isPhone ? "Enter your phone number to continue. New here? We’ll create an account." : `Please enter the code we sent to ${applyPhoneMask(phone)}.`;
  const primaryBg = resolveColor(styles.primaryButtonBgColor);
  const primaryText = resolveColor(styles.primaryButtonTextColor);
  const resendDisabled = resendCooldown > 0;
  const secondaryBorder = resolveColor(
    resendDisabled ? styles.secondaryButtonDisabledBorderColor : styles.secondaryButtonBorderColor
  );
  const secondaryText = resolveColor(styles.secondaryButtonTextColor);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: styles.dialogPadding,
        boxSizing: "border-box"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }, children: [
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }, children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  width: styles.logoContainerSize,
                  height: styles.logoContainerSize,
                  borderRadius: 0,
                  backgroundColor: resolveColor(styles.logoContainerBgColor),
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                },
                children: brandLogoUrl && /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: brandLogoUrl,
                    alt: "",
                    style: {
                      width: "100%",
                      height: "100%",
                      objectFit: "contain"
                    }
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx("p", { style: { fontSize: styles.titleFontSize, fontWeight: 600, color: titleColor, margin: 0, textAlign: "center", lineHeight: "25px" }, children: title })
          ] }),
          /* @__PURE__ */ jsx("p", { style: { fontSize: styles.subtitleFontSize, fontWeight: 500, color: subtitleColor, margin: 0, textAlign: "center", lineHeight: "1.5", maxWidth: 320 }, children: subtitle }),
          /* @__PURE__ */ jsx(
            TextField,
            {
              variant: "filled",
              fullWidth: true,
              label: isPhone ? "Phone number" : "Verification code",
              value: isPhone ? applyPhoneMask(phone) : otp,
              type: isPhone ? "tel" : "text",
              inputProps: { inputMode: isPhone ? "tel" : "numeric", maxLength: isPhone ? void 0 : 6 },
              inputRef: isPhone ? phoneInputRef : otpInputRef,
              disabled: submitting,
              onChange: isPhone ? (e) => {
                setPhone(toDigits(e.target.value).slice(0, 10));
                setError(null);
              } : (e) => {
                setOtp(e.target.value.replace(/\D/g, ""));
                setError(null);
              },
              onKeyDown: (e) => {
                if (e.key === "Enter") isPhone ? submitPhone() : verifyOtp();
              },
              slotProps: { input: { disableUnderline: true } },
              sx: {
                ".MuiInputLabel-root": {
                  fontSize: 16,
                  fontWeight: 500,
                  color: titleColor,
                  opacity: 0.5,
                  "&.Mui-focused": { color: titleColor, opacity: 0.5 },
                  "&.Mui-disabled": { color: titleColor }
                },
                ".MuiFilledInput-root": {
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "transparent" },
                  "&.Mui-focused": { backgroundColor: "transparent" },
                  "&.Mui-disabled": { backgroundColor: "transparent" },
                  border: `1px solid ${resolveColor(styles.inputBorderColor)}`,
                  borderRadius: `${styles.inputBorderRadius}px`,
                  fontSize: 14,
                  fontWeight: 500,
                  color: titleColor,
                  boxSizing: "border-box",
                  minHeight: 60,
                  // Override browser autofill blue background
                  ".MuiInputBase-input": {
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: `0 0 0 100px ${resolveColor(styles.dialogBgColor)} inset`,
                      WebkitTextFillColor: titleColor,
                      fontSize: "14px"
                    }
                  }
                }
              }
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }, children: [
          error && /* @__PURE__ */ jsx("p", { style: { width: "100%", margin: 0, fontSize: 12, color: "#dc2626", padding: "4px 8px", backgroundColor: "#fef2f2", borderRadius: 6, lineHeight: 1.4, boxSizing: "border-box" }, children: error }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              disabled: !canContinue,
              onClick: isPhone ? submitPhone : verifyOtp,
              style: {
                width: "100%",
                padding: "13px 16px",
                borderRadius: styles.primaryButtonBorderRadius,
                border: "none",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                backgroundColor: primaryBg,
                color: primaryText,
                opacity: !canContinue ? 0.3 : 1,
                cursor: !canContinue ? "default" : "pointer",
                pointerEvents: !canContinue ? "none" : "auto",
                transition: "background-color 0.15s ease, opacity 0.15s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8
              },
              children: submitting ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(Spinner, { size: 18, color: primaryText, trackColor: "rgba(255,255,255,0.35)" }),
                "Sending code…"
              ] }) : "Continue"
            }
          ),
          !isPhone && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              disabled: resendDisabled,
              onClick: resend,
              style: {
                width: "100%",
                padding: "13px 16px",
                borderRadius: styles.secondaryButtonBorderRadius,
                border: `1.5px solid ${secondaryBorder}`,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                backgroundColor: "transparent",
                color: resendDisabled ? resolveColor(styles.secondaryButtonDisabledBorderColor) : secondaryText,
                cursor: resendDisabled ? "default" : "pointer",
                transition: "all 0.15s ease"
              },
              children: resendDisabled ? `Resend code in 0:${String(resendCooldown).padStart(2, "0")}` : "Resend code"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onClose,
              style: {
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                color: resolveColor(styles.cancelTextColor),
                cursor: "pointer"
              },
              children: "Back"
            }
          )
        ] })
      ]
    }
  );
}
function PromptLoginPart({ reason: _reason, isStreaming }) {
  var _a;
  const { isSignedIn, inlineSignIn, inlineVerifyOtp, isSharedView } = useConciergeContext();
  const { data: appConfig } = useAppConfig();
  const [open, setOpen] = useState(!isStreaming);
  useEffect(() => {
    if (!isStreaming && !open) setOpen(true);
  }, [isStreaming]);
  const brandId = (_a = appConfig == null ? void 0 : appConfig.domainConfig) == null ? void 0 : _a.brandId;
  const brandLogoUrl = brandId ? getBrandLogo(brandId) : void 0;
  const homeInputConfig = useWidgetConfig("concierge-chat-input", {});
  const { themeFonts } = useThemeFonts();
  const fontFamilyCss = resolveFontFamilyCss(homeInputConfig.fontFamily, themeFonts);
  const fontData = homeInputConfig.fontFamily && homeInputConfig.fontFamily !== "inherit" ? themeFonts[homeInputConfig.fontFamily] : null;
  useEffect(() => {
    if ((fontData == null ? void 0 : fontData.format) === "google" && fontData.url) {
      loadGoogleFont(fontData.url);
    }
  }, [fontData == null ? void 0 : fontData.url]);
  if (isSignedIn || !inlineSignIn || !inlineVerifyOtp || isSharedView) return null;
  if (!open) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("style", { children: `
        @keyframes login-dialog-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @media (max-width: 899.95px) { .login-dialog-content { border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; } }
      ` }),
    /* @__PURE__ */ jsx(
      ResponsiveDialog,
      {
        open,
        onClose: (_event, reason) => {
          if (reason !== "backdropClick") setOpen(false);
        },
        disableScrollLock: true,
        sx: (theme) => ({
          zIndex: 1e4,
          "& div.MuiDialog-paper": {
            margin: "0 20px",
            width: "100%",
            maxWidth: 400,
            overflow: "visible",
            backgroundColor: "transparent",
            boxShadow: "none",
            paddingTop: 0,
            [theme.breakpoints.down("md")]: {
              maxWidth: "100%",
              width: "100%",
              margin: 0,
              padding: 0,
              paddingTop: "12px",
              boxSizing: "border-box"
            }
          }
        }),
        children: /* @__PURE__ */ jsx(WidgetEditUnsuppressProvider, { children: /* @__PURE__ */ jsx(
          LoginDialogWidget,
          {
            widgetId: "concierge-login-dialog",
            displayName: "Login Dialog",
            children: (styles, resolveColor, resolvedLogoUrl) => /* @__PURE__ */ jsx(
              "div",
              {
                className: "login-dialog-content",
                style: {
                  backgroundColor: resolveColor(styles.dialogBgColor),
                  borderRadius: styles.dialogBorderRadius,
                  boxShadow: "8px 8px 14px 0px rgba(0,0,0,0.03)",
                  border: "1px solid rgba(13,13,13,0.05)",
                  overflow: "hidden",
                  ...fontFamilyCss ? { fontFamily: fontFamilyCss } : {}
                },
                children: /* @__PURE__ */ jsx(AnimatedHeight, { children: /* @__PURE__ */ jsx(
                  LoginModalContent,
                  {
                    onClose: () => setOpen(false),
                    brandLogoUrl: resolvedLogoUrl ?? brandLogoUrl,
                    styles,
                    resolveColor
                  }
                ) })
              }
            )
          }
        ) })
      }
    )
  ] });
}
export {
  PromptLoginPart
};
//# sourceMappingURL=PromptLoginPart.js.map
